import React, { useEffect, useState } from "react";
import { Form, Modal } from "react-bootstrap";

import DstButton from "../../../components/DstButton/DstButton";
import MultiSelectDropdown from "../../../components/MultiSelectDropdown/MultiSelectDropdown";
import MultiSelectLiveDropdown from "../../../components/MultiSelectLiveDropdown/MultiSelectLiveDropdown";
import RequiredLabel from "../../../components/RequiredLabel/RequiredLabel";
import { useSession } from "../../../contexts/SessionContext";
import { LanguageEnum } from "../../../models/Language";
import translate from "../../../services/Translate";
import { createExercise, fetchInstances } from "./CreationModal.function";

const initialForm = {
  name: "",
  description: "",
  filename: "",
  language: "fr-FR",
  difficulty: 5,
  duration: 60,
  owner_id: null as number | null,
  type: "ipynb",
  instance_type_id: null as string | null,
  instance_provider: null as string | null,
  is_deprecated: false,
  origin_module_id: null as number | null,
};

const ExerciseCreationModal = ({ show, onClose, language, onSuccess }: any) => {
  const { showAlert, popAlert } = useSession();
  const [instances, setInstances] = useState<any[]>([]);
  const [owner, setOwner] = useState<any | null>(null);
  const [selectedInstance, setSelectedInstance] = useState<any | null>(null);
  const [manualInstanceType, setManualInstanceType] = useState("");
  const [moduleLabel, setModuleLabel] = useState<string>("");

  const [loading, setLoading] = useState<{
    creation: boolean;
    instances: boolean;
  }>({
    creation: false,
    instances: false,
  });

  const _typeItems = [
    { id: "html", name: "HTML" },
    { id: "ipynb", name: "IPYNB" },
    { id: "raven360", name: "RAVEN360" },
    { id: "moodle", name: "MOODLE" },
  ];

  const _instanceProviderItems = [
    { id: "aws", name: "AWS" },
    { id: "proxmox", name: "Proxmox" },
    { id: "none", name: translate(language, "PAGES.EXERCISES.CREATION_MODAL.INSTANCE_PROVIDERS.NONE") },
  ];

  const [form, setForm] = useState(initialForm);

  useEffect(() => {
    if (form.type === "html") {
      fetchInstances(language, (value: boolean) => setLoading((prev) => ({ ...prev, instances: value })), setInstances);
    }
  }, [form.type, language]);

  const _handleChange = (key: string, value: any) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const _resetForm = () => {
    setForm(initialForm);
    setOwner(null);
    setSelectedInstance(null);
    setManualInstanceType("");
    setModuleLabel("");
  };

  const _isFormInvalid =
    !form.name.trim() ||
    !form.filename.trim() ||
    !owner ||
    (form.type === "html" &&
      (!form.instance_provider ||
        (form.instance_provider === "aws"
          ? !selectedInstance
          : form.instance_provider !== "none" && !manualInstanceType.trim()))) ||
    (form.difficulty !== null && (form.difficulty < 1 || form.difficulty > 10)) ||
    (form.duration !== null && form.duration < 0);

  const _handleSubmit = () => {
    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || undefined,
      filename: form.filename.trim(),
      type: form.type,
      language: form.language,
      difficulty: typeof form.difficulty === "number" ? form.difficulty : undefined,
      duration: typeof form.duration === "number" ? form.duration : undefined,
      owner_id: Number(owner.id),
      is_deprecated: form.is_deprecated,
      ...(form.type === "html" && form.instance_provider && form.instance_provider !== "none"
        ? {
            instance_provider: form.instance_provider,
            instance_type_id: form.instance_provider === "aws" ? selectedInstance?.id : manualInstanceType.trim(),
          }
        : {}),
      ...(form.origin_module_id != null ? { origin_module_id: form.origin_module_id } : {}),
    };
    createExercise(
      language,
      payload,
      setLoading,
      (exercise: any) => {
        _resetForm();
        onSuccess(exercise);
      },
      showAlert,
      popAlert
    );
  };

  return (
    <Modal
      show={show}
      onHide={() => {
        _resetForm();
        onClose();
      }}
      centered
      size="lg"
    >
      <Modal.Header closeButton>
        <Modal.Title>{translate(language, "PAGES.EXERCISES.CREATION_MODAL.TITLE")}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Form className="p-3">
          <Form.Group className="mb-3">
            <RequiredLabel label={translate(language, "PAGES.EXERCISES.CREATION_MODAL.NAME")} />
            <Form.Control value={form.name} onChange={(event) => _handleChange("name", event.target.value)} />
          </Form.Group>

          <Form.Group className="mb-3">
            <RequiredLabel label={translate(language, "PAGES.EXERCISES.CREATION_MODAL.DESCRIPTION")} />
            <Form.Control
              as="textarea"
              rows={3}
              value={form.description}
              onChange={(event) => _handleChange("description", event.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <RequiredLabel label={translate(language, "PAGES.EXERCISES.CREATION_MODAL.FILENAME")} />
            <Form.Control value={form.filename} onChange={(event) => _handleChange("filename", event.target.value)} />
          </Form.Group>

          <Form.Group className="mb-3">
            <RequiredLabel label={translate(language, "PAGES.EXERCISES.CREATION_MODAL.LANGUAGE")} />
            <Form.Select value={form.language} onChange={(event) => _handleChange("language", event.target.value)}>
              <option value="" disabled>
                {translate(language, "PAGES.EXERCISES.CREATION_MODAL.PLACEHOLDERS.SELECT_LANGUAGE")}
              </option>

              {Object.values(LanguageEnum).map((lang) => (
                <option key={lang} value={lang}>
                  {lang}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <RequiredLabel label={translate(language, "PAGES.EXERCISES.CREATION_MODAL.DIFFICULTY")} />
            <Form.Control
              type="number"
              min={1}
              max={10}
              value={form.difficulty}
              onChange={(event) =>
                _handleChange("difficulty", event.target.value === "" ? "" : Number(event.target.value))
              }
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <RequiredLabel label={translate(language, "PAGES.EXERCISES.CREATION_MODAL.DURATION")} />
            <Form.Control
              type="number"
              min={0}
              value={form.duration}
              onChange={(event) =>
                _handleChange("duration", event.target.value === "" ? "" : Number(event.target.value))
              }
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <RequiredLabel label={translate(language, "PAGES.EXERCISES.CREATION_MODAL.TYPE")} />
            <MultiSelectDropdown
              items={_typeItems}
              language={language}
              label={_typeItems.find((type) => type.id === form.type)!.name}
              isSingleSelect
              badgeItemProperty="name"
              displayItem={(item: any) => <span>{item.name}</span>}
              onConfirm={([item]: any[]) =>
                setForm((prev) => ({
                  ...prev,
                  type: item.id,
                  instance_type_id: item.id === "html" ? prev.instance_type_id : null,
                  instance_provider: item.id === "html" ? prev.instance_provider : null,
                }))
              }
              borderToggle
              btnWidth="100%"
              toggleClass="w-100"
            />
          </Form.Group>
          {form.type === "html" && (
            <React.Fragment>
              <Form.Group className="mb-3">
                <RequiredLabel label={translate(language, "PAGES.EXERCISES.CREATION_MODAL.INSTANCE_PROVIDER")} />
                <MultiSelectDropdown
                  items={_instanceProviderItems}
                  language={language}
                  label={
                    _instanceProviderItems.find((provider) => provider.id === form.instance_provider)?.name ||
                    translate(language, "PAGES.EXERCISES.CREATION_MODAL.PLACEHOLDERS.SELECT_INSTANCE_PROVIDER")
                  }
                  isSingleSelect
                  badgeItemProperty="name"
                  displayItem={(item: any) => <span>{item.name}</span>}
                  onConfirm={([item]: any[]) => {
                    _handleChange("instance_provider", item.id);
                    setSelectedInstance(null);
                    setManualInstanceType("");
                  }}
                  borderToggle
                  btnWidth="100%"
                  toggleClass="w-100"
                />
              </Form.Group>

              {form.instance_provider && form.instance_provider !== "none" && (
                <Form.Group className="mb-3">
                  <RequiredLabel label={translate(language, "PAGES.EXERCISES.CREATION_MODAL.INSTANCE_TYPE")} />
                  {form.instance_provider === "aws" ? (
                    <MultiSelectDropdown
                      items={instances}
                      language={language}
                      label={
                        instances.find((instance) => instance.id === selectedInstance?.id)?.proper_name ||
                        translate(language, "PAGES.EXERCISES.CREATION_MODAL.PLACEHOLDERS.SELECT_INSTANCE_TYPE")
                      }
                      isSingleSelect
                      badgeItemProperty="proper_name"
                      displayItem={(item: any) => <span>{item.proper_name}</span>}
                      onConfirm={([item]: any[]) => setSelectedInstance(item ? item : null)}
                      borderToggle
                      btnWidth="100%"
                      toggleClass="w-100"
                      disabled={loading.instances}
                      isSingleSelectRemovable
                      defaultSelectedItems={instances.filter((instance) => instance.id === selectedInstance?.id)}
                    />
                  ) : (
                    <Form.Control
                      type="number"
                      value={manualInstanceType}
                      onChange={(event) => setManualInstanceType(event.target.value)}
                    />
                  )}
                </Form.Group>
              )}
            </React.Fragment>
          )}

          <Form.Group className="mb-3">
            <RequiredLabel label={translate(language, "PAGES.EXERCISES.CREATION_MODAL.OWNER")} />
            <MultiSelectLiveDropdown
              category={["users"]}
              language={language}
              isSingleSelect
              label={
                owner
                  ? `${owner.first_name} ${owner.last_name}`
                  : translate(language, "PAGES.EXERCISES.CREATION_MODAL.PLACEHOLDERS.SELECT_OWNER")
              }
              toggleClass="w-100 border"
              onConfirm={(selectedUsers: any[]) => setOwner(selectedUsers.length ? selectedUsers[0].value : null)}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            {translate(language, "PAGES.EXERCISES.CREATION_MODAL.ORIGIN_MODULE")}
            <MultiSelectLiveDropdown
              category={["modules"]}
              language={language}
              isSingleSelect
              toggleClass={`w-100 border ${!form.origin_module_id ? "text-muted" : ""}`}
              label={
                moduleLabel || translate(language, "PAGES.EXERCISES.CREATION_MODAL.PLACEHOLDERS.SELECT_ORIGIN_MODULE")
              }
              onConfirm={(selected) => {
                const module = selected?.[0]?.value ?? null;
                setModuleLabel(module ? module.name : "");
                setForm((prev) => ({ ...prev, origin_module_id: module?.id ?? null }));
              }}
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <RequiredLabel label={translate(language, "PAGES.EXERCISES.CREATION_MODAL.IS_DEPRECATED")} />
            <Form.Check
              type="switch"
              id="exercise-deprecated"
              checked={form.is_deprecated}
              onChange={(event) => _handleChange("is_deprecated", event.target.checked)}
            />
          </Form.Group>

          <div className="d-flex justify-content-end">
            <DstButton
              value={translate(language, "PAGES.EXERCISES.CREATION_MODAL.BUTTONS.CANCEL")}
              variant="light"
              btnClass="me-2"
              clickFunction={() => {
                _resetForm();
                onClose();
              }}
            />

            <DstButton
              value={translate(language, "PAGES.EXERCISES.CREATION_MODAL.BUTTONS.CREATE")}
              variant="primary"
              loading={loading.creation}
              disabled={loading.creation || _isFormInvalid}
              clickFunction={_handleSubmit}
            />
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default ExerciseCreationModal;
